import { useState } from "react";
import { Nullable } from "global";
import { useForm } from "react-hook-form";
import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { equipmentFormSchema } from "../model";
import { formDefaultValue } from "../constants";
import { mapEquipmentEntityToFormValue } from "../lib";
import { EquipmentFormMode, EquipmentFormValue } from "../types";
import { GET_LIST_OF_EQUIPMENT_BY_VO_ID } from "../api/equipment";
import { Equipment, ListOfEquipmentByVOIdResponse, ListOfEquipmentByVOIdVariables } from "../api/equipment/types";

interface LocalState {
  formMode: EquipmentFormMode;
  equipment: Nullable<Equipment>;
}

interface Options {
  id: string | undefined;
  VOId: string | undefined;
}

export function useEquipmentForm({ VOId, id }: Options) {
  const form = useForm<EquipmentFormValue>({
    mode: "onChange",
    defaultValues: formDefaultValue,
    resolver: zodResolver(equipmentFormSchema),
  });

  const [state, setState] = useState<LocalState>({ equipment: null, formMode: EquipmentFormMode.Create });

  const findEquipmentByIdAndSetFormValues = ({ equipments = [] }: ListOfEquipmentByVOIdResponse) => {
    if (!Array.isArray(equipments) || !equipments.length) return;

    const equipment = equipments.find(({ equipmentId }) => equipmentId === id);

    if (!equipment) return;

    const equipmentInitialFormValue = mapEquipmentEntityToFormValue(equipment);
    form.reset(equipmentInitialFormValue);

    setState({ equipment, formMode: EquipmentFormMode.Update });
  };

  const { loading: isLoading } = useQuery<ListOfEquipmentByVOIdResponse, ListOfEquipmentByVOIdVariables>(
    GET_LIST_OF_EQUIPMENT_BY_VO_ID,
    {
      skip: !VOId || !id,
      variables: { VOId: VOId! },
      onCompleted: findEquipmentByIdAndSetFormValues,
    }
  );

  return { form, isLoading, ...state };
}
